import { useEffect, useMemo, useState } from 'react';
import { useStateRef } from '@op/hooks/common';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { gotoDownload } from '@opm/pages/Download';
import { getBilibiliHashtagList } from '@opm/services';
import { ALL_HOURS, IBilibiliHashtagItem } from '../tabs/BilibiliTab/constants';
import { DBO_TM } from '@op/utils/format';
interface IUseBilibiliHashtagDataParams {
  filter: {
    start_time?: string;
    end_time?: string;
  } | null;
  hours: string[];
}

/**
 * Bilibili Hashtag 热搜榜数据 Hook
 *
 * 内部管理 searchText 状态，外部通过返回值绑定 SearchBox。
 * 无分页，全量加载当日数据（最多 1200 条）。
 * 请求策略参考 useBilibiliVideoData: setTimeout(0) 防双次触发。
 */
export function useBilibiliHashtagData({
  filter,
  hours,
}: IUseBilibiliHashtagDataParams) {
  const [records, setRecords] = useState<IBilibiliHashtagItem[]>([]);
  const [total, setTotal] = useState(0);
  const { wrapperCancelRequest, loading } = useCancelRequestHook({
    clear: () => {
      setRecords([]);
      setTotal(0);
    },
  });
  const [searchTextRef, searchText, setSearchText] = useStateRef('');
  const params = useMemo(() => {
    if (!filter?.start_time) return null;
    const payload: any = {
      start_time: filter.start_time,
      end_time: filter.end_time || filter.start_time,
    };
    // 非全天时传 hour_start / hour_end（整数，0~23）
    if (hours && hours.length < ALL_HOURS.length && hours.length > 0) {
      const hourNums = hours.map(h => parseInt(h, 10)).sort((a, b) => a - b);
      // 仅连续区间时传（跨午夜回绕场景不传，避免接口语义不对）
      if (hourNums[hourNums.length - 1] - hourNums[0] === hourNums.length - 1) {
        payload.hour_start = hourNums[0];
        payload.hour_end = hourNums[hourNums.length - 1];
      }
    }
    // 搜索关键词
    if (searchTextRef.current) {
      payload.search_text = searchTextRef.current;
    }
    return payload;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, hours, searchText]);

  function request() {
    if (!params) return;

    wrapperCancelRequest(getBilibiliHashtagList, params)
      .then((res: any) => {
        if (!res) return;

        // 扁平化嵌套响应: list[].time + list[].list[] → IBilibiliHashtagItem[]
        const flatRecords: IBilibiliHashtagItem[] = [];
        if (Array.isArray(res.list)) {
          res.list.forEach((group: any) => {
            const { time } = group;
            if (Array.isArray(group.list)) {
              group.list.forEach((item: any) => {
                flatRecords.push({
                  rank: item.rank,
                  hashtag: item.hashtag || '',
                  hashtag_en: item.hashtag_en || '',
                  trending_label: item.trending_label || '',
                  tag_icon: item.tag_icon || '',
                  time,
                });
              });
            }
          });
        }

        setRecords(flatRecords);
        setTotal(res.total || 0);
      })
      .catch(() => {
        // 请求取消或失败，不做处理
      });
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      request();
    }, 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  function download() {
    if (!params || !filter) return;
    gotoDownload({
      key: 'MarketingHashtagBilibiliDownload',
      fileName: `Hashtag_Bilibili_${DBO_TM.timeTitle(filter as any)}`,
      filter: params,
      data: records,
    });
  }

  return {
    records,
    total,
    loading,
    searchText,
    setSearchText,
    download,
  };
}
